import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { isEmpty } from 'lodash'
import Dashboard from './dashboard'
import { useWindowSize, scheduleLibrary } from 'passiv-libs'
import { CPI, HUM, SSO, getMessagesAndAlerts, PUSH, hooks } from 'passiv-api'

import { dayjs, useEventListener, utils, produce, useStoreLoaded } from 'passiv-libs'
import { getAwayTileContent, getLinks } from './utils'

import InOut from './_in-out'
import AwayRP from './_away'
import HotwaterModal from './hotwater-modal'

const DashboardContainer = () => {
  const store = hooks.useEventPoll("dashboard")
  window.__STORE__ = store

  const history = useHistory()

  const [state, setState] = useState({
    messagesBadge: 0,
    alerts: [],
    messages: [],
    windowWidth: 0,
    dbAppDataLoaded: false,
    dbAppData: {}
  })

  useEventListener('passiv-messages-badge', ({ detail }) => {
    setState(
      produce(draft => {
        draft.messagesBadge = detail.numUnread
      })
    )
  })

  const [width] = useWindowSize()

  useEffect(() => {
    setState(
      produce(draft => {
        draft.windowWidth = width
      })
    )
  }, [width])

  useEffect(() => {
    if (utils.cordova.isCordova()) {
      const init = async () => {
        console.log('registerPUSH PUSH.isRegistered=' + PUSH.isRegistered())
        console.log('registerPUSH getDeviceID ' + utils.settings.getDeviceID())

        if (!PUSH.isRegistered() || !utils.settings.getDeviceID()) {
          try {
            const id = await PUSH.register()
            utils.settings.setDeviceID(id)
          } catch (err) {
            console.error('Failed to register for Push Notifications: ' + err)
          }
        }
      }

      // NB delay need to compensate for Cordova load time
      setTimeout(init, 3000)
    }
  }, [])

  const setHasOnboarded = async (dbAppData) => {
    if (!utils.account.isZigbee() && !utils.account.hasCapability('immersionhotwater')) {
	const hasOnboarded = dayjs(dbAppData.home.setup_assistant_run_timestamp).isValid()
	
	if (!hasOnboarded && !utils.settings.getSetupDismissed()) {
            history.push('/setup-assistant/stage/intro')
      }

    }
  }

  const populateMessagesAndAlerts = async () => {
    const { messages, alerts } = await getMessagesAndAlerts({ messages: true, alerts: true })
    setState(
      produce(draft => {
        draft.messages = messages
        draft.alerts = alerts
        draft.messagesBadge = messages.filter(({ read }) => read === false).length
      })
    )
  }

  const populateMessagesOnly = async () => {
    await SSO.cacheCreds()
    const { messages } = await getMessagesAndAlerts({ messages: true, alerts: false })
    setState(
      produce(draft => {
        draft.messages = messages
        draft.alerts = []
        draft.messagesBadge = messages.filter(({ read }) => read === false).length
      })
    )
  }

  // If the user has heating or only hotwater capabilities (they have a hub)
  if (utils.account.hasCapability('heating') || utils.account.hasCapability('immersionhotwater')) {
    useStoreLoaded(async () => {
      if (utils.auth.isLoggedIn()) {
        await SSO.cacheCreds()
        // DEV-32267 on upgrade from Jacaranda to River UI, user doesn't relogin, but we still need to set asset url here if on mobile device
        // Copied code from app > src > containers > login > utils > setUrls
        // dev environment asset url always "", but on cordova (aka production) gets assets from different server (prod,dmz7 etc)
        if (process.env.NODE_ENV === 'production') {
          utils.settings.setAssetUrl(`${utils.settings.getServerUrl()}/passivlivingconsumer/`)
        }
        const dbAppData = await HUM.GET({})
        // Update ls.settings.tipsDismissed with DB settings
        Object.keys(dbAppData.home).forEach(prop => {
          if (prop.startsWith("modal")) {      
            utils.settings.setTipDismissed({id: prop.replace(/_/g,"-"), dismiss: (dbAppData.home[prop] === 'true')})
          }
        })
        setHasOnboarded(dbAppData)
        populateMessagesAndAlerts()
        setState(
          produce(draft => {
            draft.dbAppData = dbAppData
            draft.dbAppDataLoaded = true
          })
        )
      }
    })
  } else {
    useEffect(() => {
      populateMessagesOnly()
    }, [])
  }
  
  useEffect(() => {
    const onResumeHandler = () => {
      if (utils.account.hasCapability('heating') || utils.account.hasCapability('immersionhotwater')) {
        populateMessagesAndAlerts()
      } else {
        populateMessagesOnly()
      }
    }
    document.addEventListener('resume', onResumeHandler, false)
    return () => {
      // remove event listeners when componentName is unmounted
      document.removeEventListener('resume', onResumeHandler, false)
    }
  }, [])

  const allHeatingOccupancies = utils.heating.getAllHeatingOccupancies({
    zones: store.zones,
    heating: store.heating
  })

  const links = getLinks({
    offline: store.offline,
    messagesBadge: state.messagesBadge,
    windowWidth: state.windowWidth,
    alerts: state.alerts,
    zones: store.zones,
    groups: store.groups,
    hotwater: store.hotwater,
    schedule: store.schedule,
    occupancy_away: store.heating?.zone1.occupancy_away,
    heating: store.heating,
    awayTile: getAwayTileContent(store.heating?.zone1.occupancy_away),
    allHeatingOccupancies
  })

  return isEmpty(store) ? null : (
    <AwayRP
      render={away => (
        <InOut
          render={inOut => (
            <HotwaterModal
              render={hotwaterModal => (
                <Dashboard
                  hotwater={store.hotwater}
                  portal={{
                    display:
                      away.modal.open === 'returning'
                        ? 'modal-away-returning'
                        : away.modal.open === 'going'
                        ? 'modal-tip-away'
                        : inOut.timeModal.open
                        ? 'modal-time'
                        : inOut.tipModalIn.open
                        ? 'modal-tip-in'
                        : inOut.tipModalOut.open
                        ? 'modal-tip-out'
                        : inOut.tipModalAsleep.open
                        ? 'modal-tip-asleep'
                        : hotwaterModal.modal1.open
                        ? 'modal-hot-water-1'
                        : hotwaterModal.modal2.open
                        ? 'modal-hot-water-2'
                        : '',
                    hotwater: store.hotwater
                  }}
                  away={away}
                  inOut={inOut}
                  hotwaterModal={hotwaterModal}
                  loading={!store.loaded || away.loading || inOut.loading || hotwaterModal.loading}
                  occupancyColour={store.heating?.zone1.occupancy[0].type}
                  links={links}
                  allHeatingOccupancies={allHeatingOccupancies}
                  onClickTile={async ({ id, link }) => {
                    if (
                      (id === 'in-out' || id === 'in-out--override-out') &&
                      store.loaded &&
                      away.isAway &&
                      !utils.settings.isTipDismissed('modal-away-returning')
                    ) {
                      away.modal.toggleModal({
                        id: 'returning',
                        open: true,
                        inSetpoint: (utils.account.isZigbee()) ? store.heating?.zone1.default_setpoints.in.value : store.heating?.zone1.default_setpoints.in_am.value
                      })
                      return
                    }

                    if (id === 'in-out--override-out' && store.loaded) {
                      inOut.onClickInOutOverrideOut()
                    }

                    if (id === 'in-out' && store.loaded) {
                      inOut.onClickInOut()
                    }

                    if (id === 'hot-water' && state.dbAppDataLoaded) {
                      if (utils.account.isZigbee()) {
                        history.push(link)
                      } else {
                        const isHWInOwnGroup = (utils.account.hasCapability('immersionhotwater')) 
                          ? utils.account.hasCapability('immersionhotwater') 
                          : utils.hotwater.getGroupContainingHW({
                              groups: store.groups,
                              zones: store.zones
                            }).zoneIDs.length === 1
                                              
                        if (store.hotwater.settings.controllerMode != 'MANUAL' 
                          && !isHWInOwnGroup
                          && !state.dbAppData.home.hot_water_schedule_change_timestamp ) {
                          hotwaterModal.modal1.onOpen()
                        }
                        else if (store.hotwater.settings.controllerMode != 'MANUAL' 
                          && !isHWInOwnGroup
                          && state.dbAppData.home.hot_water_schedule_change_timestamp
                          && dayjs().diff(dayjs(state.dbAppData.home.hot_water_schedule_change_timestamp), 'month') >= 1
                          && (store.hotwater.settings.preStartLength !== store.hotwater.settings.fullHeatLength
                            || (store.hotwater.settings.noFirePeriod && store.hotwater.settings.noFirePeriod.to && store.hotwater.settings.noFirePeriod.from
                              && store.hotwater.settings.noFirePeriod.to !== '00:00:00' && store.hotwater.settings.noFirePeriod.from !== '00:00:00'))) {
                          hotwaterModal.modal2.onOpen()
                        }
                        else if (store.hotwater.settings.controllerMode != 'MANUAL' 
                          && !state.dbAppData.home.hot_water_schedule_change_timestamp) {
                          if ((store.hotwater.settings.preStartLength !== store.hotwater.settings.fullHeatLength) ||
                            (store.hotwater.settings.noFirePeriod && store.hotwater.settings.noFirePeriod.to && store.hotwater.settings.noFirePeriod.from &&
                            store.hotwater.settings.noFirePeriod.to !== '00:00:00' && store.hotwater.settings.noFirePeriod.from !== '00:00:00')) {
                            const settings = {
                              ...store.hotwater.settings,
                              preStartLength: store.hotwater.settings.fullHeatLength,
                              noFirePeriod: {
                                from: "00:00:00",
                                to: "00:00:00"
                              }
                            }
                            await CPI.PUT({
                              capability: 'hotwater',
                              property: 'hot_water_settings',
                              payload: settings
                            })
                            utils.general.triggerStoreUpdate({
                              key: 'hotwater',
                              value: { ...store.hotwater, settings }
                            })
                          }
                          await HUM.PATCH({
                            op: 'replace',
                            path: '/home/hot_water_schedule_change_timestamp',
                            value: new Date().toISOString()
                          })
                          history.push(link)
                        }
                        else {
                          history.push(link)
                        }
                      }
                    }

                    if (
                      id === 'heating' ||
                      id === 'account-settings' ||
                      id === 'alerts' ||
                      id === 'messages-alerts' ||
                      id === 'charts'
                    ) {
                      history.push(link)
                    }

                    if (id === 'away') {
                      away.onToggle()
                    }
                  }}
                />
              )}
            />
          )}
        />
      )}
    />
  )
}

export default DashboardContainer
